import { useState } from "react";
import { Check, Loader2, Clock, MessageCircle, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";
import { SoinsPortes, SoinsCibles, Soins28, BtnNeSaisPas } from "@/components/SoinsChoisir";
import { SoinsComparatif, SoinsDeroulement, SoinsFaq, SoinDuJour } from "@/components/SoinsInfos";
export { BtnNeSaisPas };

const INTRO = [
  { icon: MapPin, t: "À distance", d: "Aucun déplacement, aucun rendez-vous : nom, prénom, date de naissance et intention suffisent." },
  { icon: Clock, t: "Sous 48 heures", d: "Le soin est réalisé dans les deux jours suivant votre demande — ou chaque jour pendant 28 jours." },
  { icon: MessageCircle, t: "Retour par WhatsApp", d: "Ressentis et pistes concrètes à pratiquer vous-même. Règlement après le soin, par facture ou TWINT." },
];

const SoinsIntro = () => (
  <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-20" data-testid="soins-intro">
    <Reveal className="max-w-3xl">
      <p className="eyebrow mb-6">En bref</p>
      <p className="font-serif text-2xl sm:text-3xl text-bark leading-snug">Un soin énergétique s'adresse à ce que vous vivez : fatigue, tension, situation qui pèse, période qui demande un soutien. Il ne remplace pas un suivi médical ; il l'accompagne.</p>
    </Reveal>
    <div className="grid sm:grid-cols-3 gap-6 mt-10">
      {INTRO.map(({ icon: Icon, t, d }, i) => (
        <Reveal key={t} delay={i * 0.08}>
          <div className="border-t border-sand-dark pt-5" data-testid={`soins-intro-${i}`}>
            <Icon className="w-5 h-5 text-terra mb-3" />
            <p className="font-serif text-lg text-bark">{t}</p>
            <p className="text-sm text-fumee leading-relaxed mt-1.5">{d}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export const SoinsSections = ({ onChoisir }) => (
  <>
    <SoinsIntro />
    <SoinsPortes onChoisir={onChoisir} />
    <SoinsCibles onChoisir={onChoisir} />
    <Soins28 onChoisir={onChoisir} />
    <SoinsComparatif />
    <SoinsDeroulement />
    <SoinsFaq />
    <SoinDuJour />
  </>
);

const inputCls = "w-full bg-white/60 border border-wood/25 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-terra";

export const SoinRequestForm = ({ formule, onReset }) => {
  const [f, setF] = useState({ nom: "", prenom: "", email: "", naissance: "", intention: "", pour_moi: false, engagement: false });
  const [loading, setLoading] = useState(false);
  const [ok, setOk] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const prix = formule ? formule.prix : 50;
  const titre = formule ? formule.titre : "Soin énergétique & réalignement";
  const approfondi = formule?.id === "liberation";
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.contact({ nom: `${f.prenom} ${f.nom}`.trim(), email: f.email, sujet: "Soin énergétique", message: `Demande de soin à distance — ${titre}${prix ? ` (CHF ${prix}.–)` : " (tarif sur demande)"}\nDate de naissance : ${f.naissance}\nIntention : ${f.intention}\nPour moi-même : oui${prix ? `\nEngagement de règlement : CHF ${prix}.–` : ""}` });
      setOk(true);
    } catch {
      toast.error("Envoi impossible pour le moment — réessayez ou écrivez-nous par WhatsApp.");
    } finally {
      setLoading(false);
    }
  };
  if (ok) return (
    <div className="text-center py-8" data-testid="soin-request-success">
      <Check className="w-10 h-10 text-sage mx-auto mb-4" />
      <p className="font-serif text-2xl text-bark">Votre demande est transmise</p>
      <p className="text-fumee mt-3 max-w-md mx-auto">{approfondi ? `${titre} — Ludovic vous recontacte pour un premier échange avant toute intervention.` : `${titre} — le soin est réalisé dans les 48 heures et vous recevez un retour par WhatsApp dès qu'il est terminé.`}</p>
    </div>
  );
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="soin-request-form">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-serif text-2xl text-bark" data-testid="soin-request-titre">{titre}</p>
        <p className="font-mono text-xs text-terra" data-testid="soin-request-formule">{prix ? `CHF ${prix}.–` : "Tarif sur demande"}{formule && <button type="button" onClick={onReset} className="ml-3 text-fumee underline-offset-4 hover:underline" data-testid="soin-request-changer">changer</button>}</p>
      </div>
      <p className="text-sm text-fumee">{approfondi ? "Cette demande ouvre d'abord un échange avec Ludovic (appel, WhatsApp ou SMS) pour comprendre votre situation et vérifier que la démarche est adaptée." : "Réalisé dans les 48 heures après réception de votre demande. Retour par WhatsApp dès que le soin est terminé."}</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block"><span className="eyebrow block mb-2">Prénom *</span><input required value={f.prenom} onChange={set("prenom")} className={inputCls} data-testid="soin-prenom" /></label>
        <label className="block"><span className="eyebrow block mb-2">Nom de famille *</span><input required value={f.nom} onChange={set("nom")} className={inputCls} data-testid="soin-nom" /></label>
        <label className="block"><span className="eyebrow block mb-2">Courriel *</span><input type="email" required value={f.email} onChange={set("email")} className={inputCls} data-testid="soin-email" /></label>
        <label className="block"><span className="eyebrow block mb-2">Date de naissance *</span><input type="date" required value={f.naissance} onChange={set("naissance")} className={inputCls} data-testid="soin-naissance" /></label>
      </div>
      <label className="block"><span className="eyebrow block mb-2">{approfondi ? "Votre situation *" : "Intention du soin *"}</span><textarea required rows={4} value={f.intention} onChange={set("intention")} className={`${inputCls} resize-none`} data-testid="soin-intention" placeholder={approfondi ? "Décrivez en quelques mots ce que vous vivez, depuis quand, et ce qui a déjà été tenté…" : "Ce que vous souhaitez déposer, apaiser ou remettre en mouvement…"} /></label>
      <label className="flex items-start gap-3 text-sm text-bark"><input type="checkbox" required checked={f.pour_moi} onChange={set("pour_moi")} className="mt-1 accent-[#AE9A64]" data-testid="soin-pour-moi" />Je valide que cette demande de soin est pour moi-même.</label>
      {prix && <label className="flex items-start gap-3 text-sm text-bark"><input type="checkbox" required checked={f.engagement} onChange={set("engagement")} className="mt-1 accent-[#AE9A64]" data-testid="soin-engagement" />{approfondi ? `Je comprends que le tarif de CHF ${prix}.– s'applique si l'accompagnement est engagé après l'échange préalable (par facture ou TWINT, après le soin).` : `Je m'engage à régler CHF ${prix}.– pour ce soin à distance (par facture ou TWINT, après le soin).`}</label>}
      <button type="submit" disabled={loading} data-testid="soin-submit" className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-earth text-sand text-sm tracking-wide hover:bg-terra hover:text-earth transition-colors duration-300 disabled:opacity-50 inline-flex items-center justify-center gap-2">
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}Envoyer ma demande
      </button>
    </form>
  );
};
